import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navbar from '../components/Navbar';
import { analyticsApi } from '../services/api';

const RANGES = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

const Analytics = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [range, setRange] = useState('7d');
  const [stats, setStats] = useState(null);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [justCreated, setJustCreated] = useState(null);

  const loadSites = useCallback(async () => {
    setLoadingSites(true);
    try {
      const res = await analyticsApi.listSites();
      setSites(res.data.sites || []);
      if (!selectedSite && res.data.sites?.length) {
        setSelectedSite(res.data.sites[0].id);
      }
    } catch (err) {
      setError('Failed to load sites');
    } finally {
      setLoadingSites(false);
    }
  }, [selectedSite]);

  useEffect(() => { loadSites(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = useCallback(async () => {
    if (!selectedSite) { setStats(null); return; }
    setLoadingStats(true);
    setError('');
    try {
      const res = await analyticsApi.getStats(selectedSite, range);
      setStats(res.data);
    } catch (err) {
      setError('Failed to load stats');
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [selectedSite, range]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleCreate = async () => {
    if (!newName.trim()) { setCreateError('Name required'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const res = await analyticsApi.createSite({ name: newName.trim(), domain: newDomain.trim() });
      setJustCreated(res.data);
      setNewName('');
      setNewDomain('');
      await loadSites();
      setSelectedSite(res.data.id);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create site');
    } finally {
      setCreating(false);
    }
  };

  const embedSnippet = useMemo(() => {
    if (justCreated?.embed_snippet) return justCreated.embed_snippet;
    const site = sites.find(s => s.id === selectedSite);
    return site?.embed_snippet || '';
  }, [justCreated, selectedSite, sites]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <button
            onClick={() => { setCreateOpen(true); setJustCreated(null); setCreateError(''); }}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition"
          >
            + New site
          </button>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3 items-center">
          <label className="text-sm text-gray-600">Site</label>
          <select
            value={selectedSite}
            onChange={e => setSelectedSite(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loadingSites}
          >
            {sites.length === 0 && <option value="">No sites yet</option>}
            {sites.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s.domain ? ` (${s.domain})` : ''}
              </option>
            ))}
          </select>
          <div className="ml-auto flex gap-1 items-center">
            <button
              onClick={loadStats}
              disabled={loadingStats}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition disabled:opacity-40"
              title="Refresh"
            >
              <svg className={`w-4 h-4 ${loadingStats ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            {RANGES.map(r => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  range === r.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Embed snippet */}
        {selectedSite && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-700">
                {justCreated ? 'Embed snippet (newly created)' : 'Embed snippet'}
              </h2>
              <button
                onClick={() => navigator.clipboard?.writeText(embedSnippet)}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Copy
              </button>
            </div>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto text-gray-800">{embedSnippet}</pre>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-6">{error}</div>
        )}

        {/* Big numbers */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <BigNumber label="Pageviews" value={stats?.pageviews ?? 0} loading={loadingStats} />
          <BigNumber label="Unique sessions" value={stats?.sessions ?? 0} loading={loadingStats} />
          <BigNumber label="Unique PIDs" value={stats?.utm_pids?.length ?? 0} loading={loadingStats} />
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Pageviews over time</h2>
          <BarChart data={stats?.by_day || []} />
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DataTable title="Top paths" rows={stats?.top_paths || []} keyCol="path" valueCol="count" />
          <DataTable title="Top referrers" rows={stats?.top_referrers || []} keyCol="referrer" valueCol="count" />
          <DataTable title="Countries" rows={stats?.by_country || []} keyCol="country" valueCol="count" />
          <DataTable title="Browsers" rows={stats?.by_browser || []} keyCol="browser" valueCol="count" />
        </div>

        {/* PID tracking */}
        {stats && <PidSection pids={stats.utm_pids || []} />}
      </div>

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setCreateOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New analytics site</h3>
            <label className="block text-xs text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="My blog"
              autoFocus
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
            />
            <label className="block text-xs text-gray-600 mb-1">Domain (optional)</label>
            <input
              type="text"
              value={newDomain}
              onChange={e => setNewDomain(e.target.value)}
              placeholder="example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
            />
            {createError && <p className="text-sm text-red-600 mb-3">{createError}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition">Cancel</button>
              <button
                onClick={async () => { await handleCreate(); setCreateOpen(false); }}
                disabled={creating}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BigNumber = ({ label, value, loading }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className="text-4xl font-bold text-gray-900">
      {loading ? <span className="text-gray-300">…</span> : value.toLocaleString()}
    </p>
  </div>
);

const BarChart = ({ data }) => {
  if (!data.length) {
    return <div className="h-40 flex items-center justify-center text-sm text-gray-400">No data</div>;
  }
  const max = Math.max(1, ...data.map(d => d.count));
  const W = 100;
  const H = 40;
  const barW = W / data.length;
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-40">
        {data.map((d, i) => {
          const h = (d.count / max) * (H - 2);
          return (
            <rect
              key={i}
              x={i * barW + barW * 0.1}
              y={H - h}
              width={barW * 0.8}
              height={h}
              fill="#2563eb"
            />
          );
        })}
      </svg>
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>{formatBucket(data[0].bucket)}</span>
        <span>{formatBucket(data[data.length - 1].bucket)}</span>
      </div>
    </div>
  );
};

function formatBucket(b) {
  if (!b) return '';
  try {
    const d = new Date(b);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (e) { return ''; }
}

const PidSection = ({ pids }) => {
  const [open, setOpen] = useState(false);
  const total = pids.reduce((sum, p) => sum + p.count, 0);

  return (
    <div className="bg-white rounded-lg shadow mt-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg transition"
      >
        <span>
          Campaign PIDs
          <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            {pids.length} unique · {total.toLocaleString()} hits
          </span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {pids.length === 0 ? (
            <p className="text-sm text-gray-400">No PIDs captured in this period</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {pids.map((p, i) => (
                  <tr key={i} className="border-t border-gray-100 first:border-0">
                    <td className="py-1.5 pr-2 text-gray-700 font-mono">{p.pid}</td>
                    <td className="py-1.5 text-right text-gray-900 font-medium">{p.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const DataTable = ({ title, rows, keyCol, valueCol }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <h2 className="text-sm font-semibold text-gray-700 mb-3">{title}</h2>
    {rows.length === 0 ? (
      <p className="text-sm text-gray-400">No data</p>
    ) : (
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100 first:border-0">
              <td className="py-1.5 pr-2 text-gray-700 truncate max-w-[60%]">{r[keyCol] || '—'}</td>
              <td className="py-1.5 text-right text-gray-900 font-medium">{Number(r[valueCol]).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default Analytics;
